'use client';
/**
 * MuyuRide Realtime Subscriptions
 * Centralised helper to wire Supabase Realtime channels for all user roles.
 * Each subscription broadcasts instant notifications for:
 *  - Bid acceptance / rejection
 *  - Order status changes
 *  - Payment / escrow confirmations
 *  - Delivery milestones (pickup, in_transit, delivered, completed)
 */

import { createClient } from '@/lib/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type NotificationPayload = {
  type: 'bid_accepted' | 'bid_rejected' | 'bid_received' | 'order_status' | 'payment' | 'delivery_milestone' | 'escrow';
  title: string;
  message: string;
  referenceId?: string;
  referenceType?: string;
};

type OnNotification = (payload: NotificationPayload) => void;

interface SubscriptionOptions {
  userId: string;
  role: 'sender' | 'traveler' | 'host' | 'admin' | 'moderator';
  onNotification: OnNotification;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function mapNotifType(type: string): NotificationPayload['type'] {
  if (type === 'bid_accepted') return 'bid_accepted';
  if (type === 'bid_rejected') return 'bid_rejected';
  if (type === 'bid_received') return 'bid_received';
  if (['payment_received', 'payout_sent', 'payout_failed'].includes(type)) return 'payment';
  if (type === 'escrow_released') return 'escrow';
  if (['delivery_update', 'trip_approved'].includes(type)) return 'delivery_milestone';
  return 'order_status';
}

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const DELIVERY_MILESTONES: Record<string, { title: string; message: (route: string) => string }> = {
  payment_escrowed: {
    title: 'Payment Escrowed',
    message: (route) => `Payment secured in escrow for delivery: ${route}.`,
  },
  pickup_confirmed: {
    title: 'Parcel Picked Up',
    message: (route) => `Your parcel has been picked up and is on the way: ${route}.`,
  },
  in_transit: {
    title: 'Parcel In Transit',
    message: (route) => `Your parcel is in transit: ${route}.`,
  },
  delivered: {
    title: 'Parcel Delivered!',
    message: (route) => `Your parcel has been delivered: ${route}. Please confirm receipt.`,
  },
  completed: {
    title: 'Delivery Completed',
    message: (route) => `Delivery completed and escrow released: ${route}.`,
  },
};

/**
 * Subscribe to all relevant Realtime channels for a given user/role.
 * Returns a cleanup function that removes all channels.
 */
export function subscribeToRealtimeEvents(options: SubscriptionOptions): () => void {
  const { userId, role, onNotification } = options;
  const supabase = createClient();
  const channels: RealtimeChannel[] = [];

  // ── 1. Notifications table (all roles) ──────────────────────────────────────
  const notifChannel = supabase
    .channel(`rt-notifications-${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      (payload) => {
        const n = payload.new as any;
        onNotification({
          type: mapNotifType(n.type),
          title: n.title,
          message: n.message,
          referenceId: n.reference_id,
          referenceType: n.reference_type,
        });
      }
    )
    .subscribe();
  channels.push(notifChannel);

  // ── 2. Bids (sender: bid received; traveler: bid accepted/rejected) ──────────
  if (role === 'sender') {
    const bidsChannel = supabase
      .channel(`rt-bids-sender-${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'bids' },
        async (payload) => {
          const bid = payload.new as any;
          // Check if this bid is on one of the sender's orders
          const { data: order } = await supabase
            .from('orders')
            .select('id, pickup_city, destination_city')
            .eq('id', bid.order_id)
            .eq('sender_id', userId)
            .single();
          if (order) {
            onNotification({
              type: 'bid_received',
              title: 'New Bid Received',
              message: `A traveler submitted a bid on your order (${order.pickup_city} → ${order.destination_city}).`,
              referenceId: bid.order_id,
              referenceType: 'order',
            });
          }
        }
      )
      .subscribe();
    channels.push(bidsChannel);
  }

  if (role === 'traveler') {
    const bidsChannel = supabase
      .channel(`rt-bids-traveler-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bids',
          filter: `traveler_id=eq.${userId}`,
        },
        (payload) => {
          const bid = payload.new as any;
          if (bid.status === 'accepted') {
            onNotification({
              type: 'bid_accepted',
              title: 'Bid Accepted!',
              message: 'Your bid has been accepted. Proceed to payment to confirm the delivery.',
              referenceId: bid.order_id,
              referenceType: 'order',
            });
          } else if (bid.status === 'rejected') {
            onNotification({
              type: 'bid_rejected',
              title: 'Bid Not Selected',
              message: 'The sender selected another traveler for this order.',
              referenceId: bid.order_id,
              referenceType: 'order',
            });
          }
        }
      )
      .subscribe();
    channels.push(bidsChannel);
  }

  // ── 3. Orders (sender: own orders; admin: all orders) ───────────────────────
  if (role === 'sender') {
    const ordersChannel = supabase
      .channel(`rt-orders-sender-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `sender_id=eq.${userId}`,
        },
        (payload) => {
          const order = payload.new as any;
          onNotification({
            type: 'order_status',
            title: 'Order Updated',
            message: `Your order status changed to: ${formatStatus(order.status)}.`,
            referenceId: order.id,
            referenceType: 'order',
          });
        }
      )
      .subscribe();
    channels.push(ordersChannel);
  }

  if (role === 'admin') {
    const ordersChannel = supabase
      .channel(`rt-orders-admin-${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders' },
        (payload) => {
          const order = payload.new as any;
          onNotification({
            type: 'order_status',
            title: 'New Order Created',
            message: `New order: ${order.pickup_city} → ${order.destination_city}.`,
            referenceId: order.id,
            referenceType: 'order',
          });
        }
      )
      .subscribe();
    channels.push(ordersChannel);
  }

  // ── 4. Deliveries (sender + traveler: own deliveries; admin: all) ────────────
  if (role === 'sender' || role === 'traveler') {
    const deliveriesChannel = supabase
      .channel(`rt-deliveries-${role}-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'deliveries',
          filter: role === 'sender' ? `sender_id=eq.${userId}` : `traveler_id=eq.${userId}`,
        },
        (payload) => {
          const delivery = payload.new as any;
          const milestone = DELIVERY_MILESTONES[delivery.status as string];
          if (milestone) {
            onNotification({
              type: 'delivery_milestone',
              title: milestone.title,
              message: milestone.message(delivery.route || `${delivery.pickup_city} → ${delivery.destination_city}`),
              referenceId: delivery.id,
              referenceType: 'delivery',
            });
          }
        }
      )
      .subscribe();
    channels.push(deliveriesChannel);
  }

  // ── 5. Transactions / payments (sender + traveler: own; admin: all) ──────────
  if (role === 'sender' || role === 'traveler') {
    const txChannel = supabase
      .channel(`rt-transactions-${role}-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'transactions',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const tx = payload.new as any;
          if (tx.status === 'released') {
            onNotification({
              type: 'escrow',
              title: 'Escrow Released',
              message: `KES ${tx.amount?.toLocaleString()} has been released from escrow.`,
              referenceId: tx.id,
              referenceType: 'transaction',
            });
          } else if (tx.status === 'escrowed') {
            onNotification({
              type: 'payment',
              title: 'Payment Confirmed',
              message: `KES ${tx.amount?.toLocaleString()} is now held in escrow.`,
              referenceId: tx.id,
              referenceType: 'transaction',
            });
          } else if (tx.status === 'refunded') {
            onNotification({
              type: 'payment',
              title: 'Payment Refunded',
              message: `KES ${tx.amount?.toLocaleString()} has been refunded.`,
              referenceId: tx.id,
              referenceType: 'transaction',
            });
          }
        }
      )
      .subscribe();
    channels.push(txChannel);
  }

  // ── 6. Tracking events (sender + traveler: own deliveries) ──────────────────
  if (role === 'sender' || role === 'traveler') {
    const trackingChannel = supabase
      .channel(`rt-tracking-${role}-${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'tracking_events' },
        async (payload) => {
          const event = payload.new as any;
          const col = role === 'sender' ? 'sender_id' : 'traveler_id';
          const { data: delivery } = await supabase
            .from('deliveries')
            .select('id, route, pickup_city, destination_city')
            .eq('id', event.delivery_id)
            .eq(col, userId)
            .single();
          if (delivery) {
            onNotification({
              type: 'delivery_milestone',
              title: event.title || 'Tracking Update',
              message: event.description || `Update on ${delivery.route || `${delivery.pickup_city} → ${delivery.destination_city}`}.`,
              referenceId: delivery.id,
              referenceType: 'delivery',
            });
          }
        }
      )
      .subscribe();
    channels.push(trackingChannel);
  }

  // ── Cleanup ──────────────────────────────────────────────────────────────────
  return () => {
    channels.forEach(ch => supabase.removeChannel(ch));
  };
}
